import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, Box, Check, ChevronDown, Code2, Eye, FileArchive, FileCode2, Globe, Lock, Mic, Paperclip, Sparkles, Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import { API } from "@/App";
import { VantaMark } from "@/components/vai/Message";

// Page de Vanta AI pour un visiteur non connecté : ce que fait l'IA, les deux
// modèles (Pulse gratuit, Vector · Vanta Code payant), les offres (lues sur le
// site) et les questions fréquentes. Tout bouton ouvre l'inscription.

const FEATURES = [
  { icon: FileArchive, title: "Projets en .zip", text: "Ressource FiveM, bot Discord, jeu Roblox, site : tous les fichiers, prêts à télécharger." },
  { icon: Eye, title: "Aperçu en direct", text: "Pages web et composants s'affichent à côté de la conversation, et se modifient en un message." },
  { icon: Paperclip, title: "Fichiers et photos", text: "Envoie ton code, ton .zip ou une capture d'écran d'erreur : l'IA les lit avant de répondre." },
  { icon: Mic, title: "Dictée", text: "Parle au lieu d'écrire : ta voix devient un message, que tu relis avant d'envoyer." },
  { icon: Globe, title: "Recherche web", text: "Des réponses à jour, avec leurs sources, quand la question le demande." },
  { icon: Box, title: "Images et 3D", text: "Images à partir d'une description, et modèles 3D Blender (.glb) à télécharger (3D : Pro et Max)." },
];

const FAQ = [
  ["C'est vraiment gratuit ?", "Oui : Pulse, la recherche web, les images, les fichiers, la dictée et les pages web sont inclus dans l'offre gratuite, avec une limite d'utilisation qui se recharge toute seule. Vector · Vanta Code et la 3D sont dans les offres Pro et Max."],
  ["Quelle différence entre Pulse et Vector ?", "Pulse est petit et rapide, pour les questions du quotidien. Vector (Vanta Code) est plus grand et entraîné par VantaPulse : c'est lui qu'il faut pour les projets de code complets."],
  ["Où vont mes conversations ?", "Sur les serveurs de VantaPulse : les modèles tournent chez nous. Tu peux tout supprimer à tout moment dans les Réglages."],
  ["Il répond aussi vite que les grosses IA ?", "Non, et on préfère le dire : nos modèles tournent sur nos propres serveurs, sans les milliers de cartes graphiques des géants. Il écrit au fil de l'eau, et le premier message peut prendre quelques secondes de plus."],
];

function Demo() {
  return (
    <div className="relative mx-auto max-w-3xl" aria-hidden="true">
      <div className="absolute -inset-8 bg-gradient-to-r from-blue-600/20 via-violet-600/20 to-fuchsia-600/10 blur-3xl rounded-[40px]" />
      <div className="relative rounded-2xl border border-white/10 bg-[#111118]/95 shadow-2xl shadow-black/60 overflow-hidden">
        <div className="flex items-center gap-1.5 px-4 h-10 border-b border-white/[0.06]">
          <span className="w-2.5 h-2.5 rounded-full bg-white/15" /><span className="w-2.5 h-2.5 rounded-full bg-white/15" /><span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          <span className="ml-3 text-xs text-white/40">Vanta AI</span>
        </div>
        <div className="grid md:grid-cols-[1fr_260px]">
          <div className="p-5 space-y-4 text-left">
            <div className="flex justify-end"><div className="rounded-2xl bg-white/[0.07] px-4 py-2.5 text-sm max-w-[85%]">Crée une ressource FiveM de garage, en .zip</div></div>
            <div className="flex gap-3">
              <VantaMark className="w-6 h-6" />
              <div className="text-sm text-white/80 space-y-3 min-w-0">
                <p>Voici la ressource <strong className="text-white">garage</strong> : sortie et rangement des véhicules, avec un menu.</p>
                <div className="flex items-center gap-3 rounded-xl border border-blue-400/40 bg-blue-500/10 px-3 py-2.5">
                  <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center"><FileArchive className="w-4 h-4 text-sky-300" /></span>
                  <span><span className="block text-sm font-medium text-white">Garage FiveM</span><span className="block text-xs text-white/45">Projet (.zip) · 4 fichiers</span></span>
                </div>
              </div>
            </div>
          </div>
          <div className="hidden md:block border-l border-white/[0.06] bg-black/20 p-3 text-left">
            <p className="text-[11px] text-white/40 mb-2">garage/</p>
            {["fxmanifest.lua", "config.lua", "client/main.lua", "server/main.lua"].map((f, i) => (
              <p key={f} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs ${i === 2 ? "bg-white/10 text-white" : "text-white/55"}`}><FileCode2 className="w-3.5 h-3.5" />{f}</p>
            ))}
            <p className="mt-3 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.06] text-[11px] text-white/70"><FileArchive className="w-3.5 h-3.5" />Télécharger le .zip</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VantaAILanding() {
  const [auth, setAuth] = useState(false);
  const [mode, setMode] = useState("login");
  const [plans, setPlans] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const open = (m) => { setMode(m); setAuth(true); };
  useEffect(() => { axios.get(`${API}/vai/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {}); }, []);

  return (
    <div className="relative z-[2] min-h-screen bg-[#0b0b10] text-white overflow-x-hidden" data-testid="vai-landing">
      <Navbar onAuthClick={() => open("login")} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[640px] bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.22),transparent)]" />

      <main className="relative">
        <section className="max-w-5xl mx-auto px-4 pt-32 sm:pt-36 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70">
            <Sparkles className="w-3.5 h-3.5 text-violet-300" />Nouveau : Vector · Vanta Code
          </span>
          <h1 className="mt-6 text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.05]">
            L'IA qui code<br className="hidden sm:block" /> <span className="bg-gradient-to-r from-sky-300 via-blue-400 to-violet-400 bg-clip-text text-transparent">pour tes serveurs</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/60 max-w-2xl mx-auto">
            Scripts FiveM et Roblox, bots Discord, sites avec aperçu en direct : Vanta AI écrit, corrige et te rend des projets prêts à télécharger en .zip.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button type="button" onClick={() => open("register")} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-black font-medium hover:bg-white/90" data-testid="vai-landing-register">
              Commencer gratuitement<ArrowRight className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => open("login")} className="px-6 py-3 rounded-xl border border-white/15 hover:bg-white/5">Se connecter</button>
          </div>
          <p className="mt-3 text-xs text-white/40">Sans carte bancaire · Pulse gratuit pour toujours</p>
          <div className="mt-14"><Demo /></div>
        </section>

        <section className="max-w-5xl mx-auto px-4 mt-28">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-center">Deux modèles, faits maison</h2>
          <p className="mt-2 text-center text-white/55">Ils tournent sur les serveurs de VantaPulse, pas chez une autre entreprise.</p>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex items-center gap-2"><Zap className="w-5 h-5 text-sky-300" /><h3 className="text-lg font-semibold">Pulse</h3><span className="ml-auto text-xs px-2 py-0.5 rounded-full border border-white/15 text-white/70">Gratuit</span></div>
              <p className="mt-3 text-sm text-white/60">Petit et rapide, pour le quotidien : questions, textes, petits bouts de code, pages web.</p>
            </div>
            <div className="relative rounded-2xl border border-violet-400/30 bg-gradient-to-b from-violet-500/[0.08] to-transparent p-6">
              <div className="flex items-center gap-2"><Code2 className="w-5 h-5 text-violet-300" /><h3 className="text-lg font-semibold">Vector</h3>
                <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500/25 to-violet-500/25 border border-violet-400/30">Vanta Code</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-violet-400/30 text-violet-200"><Lock className="w-3 h-3" />Pro et Max</span></div>
              <p className="mt-3 text-sm text-white/60">Le plus capable, entraîné par VantaPulse pour le code : projets complets en .zip, corrections, 3D.</p>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 mt-24">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-center">Tout ce qu'il te faut, au même endroit</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 hover:border-white/15 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center"><f.icon className="w-4 h-4 text-sky-300" /></span>
                  {f.pro && <span className="text-[11px] px-1.5 py-0.5 rounded-md border border-violet-400/30 bg-violet-500/10 text-violet-200">Pro et Max</span>}
                </div>
                <p className="mt-4 font-medium">{f.title}</p>
                <p className="mt-1 text-sm text-white/55 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </section>

        {plans.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 mt-24" data-testid="vai-landing-plans">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-center">Des offres simples</h2>
            <p className="mt-2 text-center text-white/55">Sans engagement, annulables à tout moment.</p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {plans.map((p) => {
                const featured = p.id === "pro";
                return (
                  <div key={p.id} className={`relative rounded-2xl border p-6 flex flex-col ${featured ? "border-blue-400/40 bg-gradient-to-b from-blue-500/[0.08] to-transparent" : "border-white/10 bg-white/[0.02]"}`}>
                    {featured && <span className="absolute -top-3 left-6 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-600">Le plus choisi</span>}
                    <h3 className="text-lg font-semibold">{p.name}</h3>
                    <p className="mt-3"><span className="text-4xl font-semibold">{p.price ? String(p.price).replace(".", ",") : "0"} €</span><span className="text-white/45 text-sm"> / mois</span></p>
                    <ul className="mt-5 space-y-2 text-sm text-white/75 flex-1">
                      {p.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-sky-300 shrink-0" />{f}</li>)}
                    </ul>
                    <button type="button" onClick={() => open("register")} className={`mt-6 py-2.5 rounded-xl text-sm font-medium ${featured ? "bg-gradient-to-r from-blue-500 to-violet-600 hover:brightness-110" : "border border-white/15 hover:bg-white/5"}`}>
                      {p.price ? `Choisir ${p.name}` : "Commencer"}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="max-w-3xl mx-auto px-4 mt-24">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-center">Questions fréquentes</h2>
          <div className="mt-8 divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {FAQ.map(([q, a], i) => (
              <div key={q}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-4 text-left" aria-expanded={openFaq === i}>
                  <span className="font-medium">{q}</span><ChevronDown className={`w-4 h-4 text-white/50 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="pb-4 -mt-1 text-sm text-white/60 leading-relaxed">{a}</p>}
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 mt-24 pb-24">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-blue-600/20 via-violet-600/15 to-transparent px-6 py-12 text-center">
            <VantaMark className="w-12 h-12 mx-auto" />
            <h2 className="mt-5 text-2xl sm:text-3xl font-semibold tracking-tight">Prêt à coder plus vite ?</h2>
            <p className="mt-2 text-white/60">Crée ton compte et pose ta première question en 30 secondes.</p>
            <button type="button" onClick={() => open("register")} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-black font-medium hover:bg-white/90">Commencer gratuitement<ArrowRight className="w-4 h-4" /></button>
          </div>
        </section>
      </main>
      <AuthModal isOpen={auth} onClose={() => setAuth(false)} mode={mode} setMode={setMode} />
    </div>
  );
}
